import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Scissors, Sparkles, MapPin, ArrowRight, Star, CalendarCheck, Play, BadgeCheck } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api, waLink } from "../lib/constants";
import { Reveal } from "../components/Reveal";
import { Overline, SectionTitle, GoldButton, OutlineButton } from "../components/site/Primitives";
import IndiqueGanhe from "../components/site/IndiqueGanhe";
import SEO from "../components/SEO";

const CITIES = [
  { name: "Arapiraca", to: "/protese-capilar-arapiraca", desc: "Barbearia das Estrelas + prótese capilar" },
  { name: "Garanhuns", to: "/protese-capilar-garanhuns", desc: "Prime Capilar Clinic" },
  { name: "Maceió", to: "/protese-capilar-maceio", desc: "Parceria com a Arena Barbearia" },
];

export default function Home() {
  const { site } = useSite();
  const [results, setResults] = useState([]);
  const hero = site?.hero || {};
  const img = site?.images || {};

  useEffect(() => {
    api.get("/results").then(({ data }) => setResults(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      <SEO title="Barbearia das Estrelas | Prime Capilar Clinic — Barbearia e Prótese Capilar" description="Barbearia, cuidado masculino e prótese capilar em Arapiraca, Garanhuns e Maceió. Agende seu horário ou faça uma avaliação capilar." />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero.image} alt="Barbearia das Estrelas" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
          <Reveal>
            <Overline className="mb-5 flex items-center gap-2"><Star className="w-3.5 h-3.5 fill-gold" /> Barbearia · Cuidado Masculino · Prótese Capilar</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-heading font-700 uppercase tracking-tight text-white text-4xl sm:text-6xl lg:text-[5rem] leading-[0.98] max-w-4xl">
              {hero.title_line1 || "MAIS QUE ESTÉTICA."}<br />
              <span className="text-gold">{hero.title_line2 || "UMA NOVA VERSÃO DE VOCÊ."}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-zinc-300 text-lg max-w-xl leading-relaxed">
              {hero.subtitle || "Barbearia, cuidado masculino e soluções capilares em um só lugar."}
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <GoldButton href={waLink(site, "barbearia")} size="lg" data-testid="home-agendar-barbearia-button">
                <CalendarCheck className="w-5 h-5" /> AGENDAR NA BARBEARIA
              </GoldButton>
              <OutlineButton to="/protese-capilar" size="lg" data-testid="home-avaliacao-capilar-button">
                QUERO FAZER UMA AVALIAÇÃO CAPILAR <ArrowRight className="w-4 h-4" />
              </OutlineButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TWO PILLARS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <Reveal><Overline className="mb-3">Duas áreas, um só lugar</Overline></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <Reveal>
            <div className="group relative overflow-hidden rounded-sm border border-white/12 h-full">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={img.barbershop_interior} alt="Barbearia das Estrelas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7 lg:p-9 bg-[#0d0d0d]">
                <div className="flex items-center gap-2 text-gold mb-3"><Scissors className="w-5 h-5" /><span className="overline-label text-xs">Barbearia principal</span></div>
                <h3 className="font-heading font-700 uppercase text-2xl lg:text-3xl text-white">Barbearia das Estrelas</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">Corte, barba, estilo e cuidado masculino com profissionais especializados.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <GoldButton href={waLink(site, "barbearia")} data-testid="home-barbearia-agendar">AGENDAR HORÁRIO</GoldButton>
                  <OutlineButton to="/barbearia">Ver serviços</OutlineButton>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="group relative overflow-hidden rounded-sm border border-white/12 h-full">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={img.clinic_scalp} alt="Prime Capilar Clinic" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7 lg:p-9 bg-[#0d0d0d]">
                <div className="flex items-center gap-2 text-gold mb-3"><Sparkles className="w-5 h-5" /><span className="overline-label text-xs">Prótese capilar</span></div>
                <h3 className="font-heading font-700 uppercase text-2xl lg:text-3xl text-white">Prime Capilar Clinic</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">Atendimento especializado em prótese capilar masculina, desde a avaliação até a aplicação e manutenção.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <GoldButton to="/protese-capilar" data-testid="home-protese-avaliacao">QUERO FAZER UMA AVALIAÇÃO</GoldButton>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Prótese cities */}
        <Reveal>
          <div className="mt-8 border border-white/12 rounded-sm bg-[#0b0b0b] p-7 lg:p-9">
            <p className="text-white font-heading uppercase tracking-wide text-lg mb-5">Atendimento de prótese capilar em:</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {CITIES.map((c) => (
                <Link key={c.name} to={c.to} data-testid={`home-city-${c.name.toLowerCase()}`} className="group flex items-center justify-between border border-white/10 hover:border-gold rounded-sm px-5 py-4 transition-colors">
                  <div>
                    <div className="flex items-center gap-2 text-white font-heading text-lg"><MapPin className="w-4 h-4 text-gold" /> {c.name}</div>
                    <p className="text-xs text-zinc-500 mt-1">{c.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-gold transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* RESULTS TEASER */}
      <section className="bg-[#080808] border-y border-white/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <Reveal><Overline className="mb-3">Transformações</Overline></Reveal>
              <Reveal delay={0.05}><SectionTitle>Resultados reais</SectionTitle></Reveal>
            </div>
            <Reveal delay={0.1}><OutlineButton to="/resultados">Ver galeria <ArrowRight className="w-4 h-4" /></OutlineButton></Reveal>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-10">
            {results.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.05}>
                <div className="relative aspect-[3/4] overflow-hidden rounded-sm border border-white/10 group bg-[#0a0a0a]">
                  <img src={r.image} alt={r.title} className={`w-full h-full transition-transform duration-700 group-hover:scale-110 ${r.fit === "contain" ? "object-contain" : "object-cover"}`} />
                  {!r.video && ((r.title || "").toLowerCase().includes("antes e depois") || (r.title || "").toLowerCase().includes("prótese") || (r.title || "").toLowerCase().includes("protese")) && (
                    <div className="absolute top-1.5 left-1.5 flex items-center gap-1 bg-black/70 backdrop-blur-sm border border-gold/50 text-gold rounded-full pl-1.5 pr-2 py-0.5 shadow-lg">
                      <BadgeCheck className="w-3 h-3" />
                      <span className="font-heading font-600 uppercase tracking-wide text-[9px]">Real</span>
                    </div>
                  )}
                  {r.video && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="w-11 h-11 rounded-full bg-gold/90 text-black flex items-center justify-center shadow-lg"><Play className="w-5 h-5 ml-0.5 fill-black" /></span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLUB TEASER */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-sm border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-9 lg:p-14">
          <Reveal><Overline className="mb-3">Clube de assinatura</Overline></Reveal>
          <Reveal delay={0.05}><SectionTitle className="max-w-2xl">Seu visual sempre em dia pagando uma mensalidade fixa</SectionTitle></Reveal>
          <Reveal delay={0.1}><p className="mt-4 text-zinc-300 max-w-xl leading-relaxed">Planos de corte, barba e planos para a família inteira. Escolha o seu e mantenha o estilo sempre alinhado.</p></Reveal>
          <Reveal delay={0.15}><div className="mt-7"><GoldButton to="/clube" size="lg" data-testid="home-clube-button">CONHECER O CLUBE <ArrowRight className="w-4 h-4" /></GoldButton></div></Reveal>
        </div>
      </section>

      {/* INDIQUE E GANHE */}
      <IndiqueGanhe className="pb-20 lg:pb-28" />
    </div>
  );
}
